import React from 'react';

/** Below Tailwind's `sm`, the app's phone breakpoint. */
const NARROW_QUERY = '(max-width: 639px)';

/**
 * Whether the viewport is narrower than Tailwind's `sm`: a phone.
 *
 * A media query rather than a `sm:` class, for the places where the two
 * widths differ in structure or props, not in style: which columns the
 * products grid pins (a column-definition prop that feeds DataGrid's own
 * offset arithmetic), or whether the catalog import's Products step renders
 * its rows as a grid or as cards (rendering both and hiding one would mount
 * every row twice and put both in the accessibility tree).
 *
 * Read on the first render, not after it: a phone would otherwise paint the
 * desktop form once and then jump. Returns `false` where `matchMedia` is
 * missing, so a test that never sized a viewport gets the desktop form.
 */
const matchesNarrow = (): boolean =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia(NARROW_QUERY).matches;

export const useIsNarrowViewport = (): boolean => {
    const [isNarrow, setIsNarrow] = React.useState(matchesNarrow);

    React.useEffect(() => {
        if (typeof window.matchMedia !== 'function') {
            return;
        }

        const query = window.matchMedia(NARROW_QUERY);
        setIsNarrow(query.matches);

        const onChange = (event: MediaQueryListEvent) =>
            setIsNarrow(event.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    return isNarrow;
};
